import { useRef, useState } from 'react';
import { validiteParDefaut } from '../../lib/echeances';
import { useReglages } from '../../api/reglages.jsx';
import { iaApi, repriseApi } from '../../api/resources';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { idProvisoire } from '../../lib/identifiants';
import { DEVISE, fmt } from '../../lib/currency';
import { prepareFichiers } from '../../lib/document-ia';
import { lisPiece } from '../../lib/reprise-texte';
import { messageIa } from '../../lib/ia';
import { uniteDevis } from '../../lib/offre-ia';
import { CLE_PACK_IA, packIaActif } from '../../lib/pack-ia';
import { verifieFichiers } from '../../lib/soumission';
import { tr, trf } from '../../lib/i18n';
import { tauxTvaParDefaut } from '../../lib/tva';
import { remiseVisantLeTotal, totauxDocument } from '../../lib/vat';
import { litDate } from '../../lib/periodes';
import { nextSeq, today, uid } from '../../lib/utils';
import { C, DA } from '../../theme/palette';

/**
 * Délai de paiement d'une facture reprise, tel que l'écran l'écrit
 * (« 30 jours », « À réception »), d'après son échéance datée ou le délai lu.
 *
 * @param {string} date date de la facture (JJ.MM.AAAA)
 * @param {string} [echeance] échéance lue (JJ.MM.AAAA)
 * @param {number} [delaiJours] délai lu, en jours
 * @returns {string} le délai
 */
export function delaiDeReprise(date, echeance, delaiJours) {
  const d = litDate(date);
  const e = echeance ? litDate(echeance) : null;
  if (d && e) {
    const jours = Math.round((e.getTime() - d.getTime()) / 86400000);
    if (jours === 0) return 'À réception';
    if (jours > 0 && jours <= 365) return jours + ' jours';
  }
  return delaiJours > 0 ? delaiJours + ' jours' : '30 jours';
}

export function ImportPage({
  onNav,
  soc,
  devis,
  setDevis,
  factures,
  setFactures,
  clients,
  setClients,
  estMixte,
  univers,
  setUnivers,
}) {
  const estMag = estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
  const [type, setType] = useState('offre');
  // Vrai dès que le patron a touché « Offre » ou « Facture » : son choix
  // l'emporte. Sinon, le titre de l'ancien document décide — une pièce
  // titrée « FACTURE » devenait une offre, le choix par défaut.
  const typeChoisi = useRef(false);
  const choisitType = (v) => {
    typeChoisi.current = true;
    setType(v);
  };
  const [raw, setRaw] = useState('');
  const [cli, setCli] = useState({ nom: '', adr: '', npa: '' });
  const [lignes, setLignes] = useState([]);
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(null);
  // La reprise de plusieurs pièces d'un coup, et ce qu'elle a donné.
  const [lot, setLot] = useState(null);
  // Pack IA : le client reconnu par la lecture d'une photo ou d'un scan, et ce
  // que le serveur a relevé sur le document.
  const { reglageJson } = useReglages();
  const packIa = packIaActif(reglageJson(CLE_PACK_IA, null));
  const entreeScan = useRef(null);
  const [cliId, setCliId] = useState('');
  const [alertes, setAlertes] = useState([]);
  const [lecture, setLecture] = useState('');
  // Numéro et date de l'ancienne pièce : la date compte pour la comptabilité.
  const [origine, setOrigine] = useState({ numero: '', date: '' });
  // Le dernier PDF lu, que le Pack IA peut relire quand le texte ne donne
  // aucun article.
  const [pdfLu, setPdfLu] = useState(null);
  const LECTURE_EN_COURS = 'en cours';
  const LECTURE_PDF = 'lecture du PDF';

  /** Lit la photo ou le scan d'un document papier, et passe à la vérification. */
  const litLeScan = async (fichiers) => {
    const erreur = verifieFichiers(fichiers, { texteAdmis: false, document: 'Un document' });
    if (erreur) {
      setLecture(erreur);
      return;
    }
    setLecture(LECTURE_EN_COURS);
    try {
      const lu = await iaApi.lectureDocument({ fichiers: await prepareFichiers(fichiers) });
      // Le bouton « Offre » ou « Facture » choisi décide, pas le texte lu.
      const c = lu.client || {};
      setCli({ nom: c.nom || '', adr: c.adresse || '', npa: c.npa || '' });
      setCliId(c.existant ? c.existant.id : '');
      const lues = (lu.lignes || []).map((l) => ({
        id: uid(),
        d: l.designation || '',
        q: l.quantite ?? 1,
        u: uniteDevis(l.unite) || 'pce',
        p: l.prixUnitaire ?? 0,
        rem: 0,
        alertes: l.alertes || [],
      }));
      setLignes(lues.length ? lues : [{ id: uid(), d: '', q: 1, u: 'pce', p: 0, rem: 0 }]);
      setAlertes(lu.alertes || []);
      setLecture('');
      setStep(2);
    } catch (e) {
      setLecture(e?.status != null ? messageIa(e) : e?.message || messageIa(e));
    }
  };
  const num2 = (s) =>
    parseFloat(
      String(s)
        .replace(/['\s\u00a0]/g, '')
        .replace(',', '.'),
    ) || 0;
  /** Ce que l'ancienne pièce dit d'elle-même : numéro, date, remise, paiement. */
  const origineDe = (lu) => ({
    numero: lu.numero,
    date: lu.date,
    remisePct: lu.remisePct || 0,
    acomptePct: lu.acomptePct || 0,
    totalTtc: lu.totalTtc || 0,
    echeance: lu.echeance || '',
    delaiJours: lu.delaiJours || 0,
    // « Payé » sur l'ancienne facture : elle arrive payée. La date du
    // paiement n'y figure pas ; celle de la facture la remplace, à corriger.
    payee: lu.paye > 0,
    payeeLe: lu.paye > 0 ? lu.date || '' : '',
    montantPaye: lu.paye || 0,
    remarques: lu.remarques || '',
  });
  const optionsLecture = () => ({
    npaSociete: soc?.npa || '',
    nomSociete: soc?.nom || '',
    adrSociete: soc?.adr || '',
  });
  // Lecture commune au texte collé et au PDF : client (libellé « Facturé à »
  // ou bloc d'adresse du PDF), lignes — même celles dont les chiffres sont
  // sous la désignation —, date et numéro d'origine.
  const analyser = (texteSource) => {
    const lu = lisPiece(typeof texteSource === 'string' ? texteSource : raw, {
      npaSociete: soc?.npa || '',
      nomSociete: soc?.nom || '',
      adrSociete: soc?.adr || '',
    });
    // Le patron a touché « Offre » ou « Facture » : c'est ce qui se crée,
    // même si l'ancien document dit « Facturé à » ou parle de facture.
    if (!typeChoisi.current && lu.genre) setType(lu.genre);
    setOrigine(origineDe(lu));
    setCli(lu.client);
    setCliId('');
    setAlertes([]);
    setLignes(
      lu.lignes.length
        ? lu.lignes.map((l) => ({ ...l, id: uid() }))
        : [{ id: uid(), d: '', q: 1, u: 'pce', p: 0, rem: 0 }],
    );
    setStep(2);
    return lu;
  };
  /**
   * Avec le Pack IA, ce que la lecture du texte n'a pas trouvé — articles,
   * client, adresse — est complété tout seul par la lecture du document, sans
   * bouton à toucher. Ce qui a été lu reste ; seuls les vides sont remplis.
   */
  const completeAvecIa = async (f, lu) => {
    const manque =
      !(lu.lignes || []).length || !(lu.client?.nom || '').trim() || !(lu.client?.adr || '').trim();
    if (!packIa || !manque) return;
    setLecture(LECTURE_EN_COURS);
    try {
      const ia = await iaApi.lectureDocument({ fichiers: await prepareFichiers([f]) });
      const c = ia.client || {};
      setCli((p) => ({
        nom: (p.nom || '').trim() ? p.nom : c.nom || '',
        adr: (p.adr || '').trim() ? p.adr : c.adresse || '',
        npa: (p.npa || '').trim() ? p.npa : c.npa || '',
      }));
      if (c.existant && !(lu.client?.nom || '').trim()) setCliId(c.existant.id);
      const lues = (ia.lignes || []).map((l) => ({
        id: uid(),
        d: l.designation || '',
        q: l.quantite ?? 1,
        u: uniteDevis(l.unite) || 'pce',
        p: l.prixUnitaire ?? 0,
        rem: 0,
        alertes: l.alertes || [],
      }));
      // Les articles du Pack IA remplacent la ligne de secours (le seul total).
      const seulementLeTotal =
        (lu.lignes || []).length === 1 && /^Travaux selon /.test(lu.lignes[0].d);
      if (lues.length && (!(lu.lignes || []).length || seulementLeTotal)) setLignes(lues);
      setAlertes(ia.alertes || []);
      setLecture('');
    } catch (e) {
      setLecture(e?.status != null ? messageIa(e) : e?.message || messageIa(e));
    }
  };
  /** Un PDF est lu par le serveur, colonnes conservées ; un .csv ou .txt, ici. */
  const litFichier = async (f) => {
    if (!f) return;
    if (/\.pdf$/i.test(f.name || '') || f.type === 'application/pdf') {
      setPdfLu(f);
      setLecture(LECTURE_PDF);
      try {
        const { texte } = await repriseApi.texteDuPdf(f, f.name);
        setRaw(texte);
        setLecture('');
        const lu = analyser(texte);
        await completeAvecIa(f, lu);
      } catch (e) {
        setLecture(e?.message || 'Ce PDF n’a pas pu être lu.');
      }
      return;
    }
    const r = new FileReader();
    r.onload = () => setRaw(String(r.result || ''));
    r.readAsText(f);
  };
  /**
   * Plusieurs anciennes pièces d'un coup : chacune est lue et créée toute
   * seule, à sa date d'origine — une facture de 2024 compte en 2024 — et au
   * taux de TVA de son année. Celles qui ne se lisent pas restent « à
   * vérifier », reprises ensuite une par une.
   */
  const litPlusieurs = async (fichiers) => {
    const liste = [...fichiers].slice(0, 100);
    setLot({ total: liste.length, faits: [], enCours: true });
    const cache = { clients: clients || [] };
    for (const f of liste) {
      let res;
      try {
        const estPdf = /\.pdf$/i.test(f.name || '') || f.type === 'application/pdf';
        const texte = estPdf ? (await repriseApi.texteDuPdf(f, f.name)).texte : await f.text();
        const lu = lisPiece(texte, optionsLecture());
        let client = { ...lu.client };
        let articles = lu.lignes;
        const seulementLeTotal = articles.length === 1 && /^Travaux selon /.test(articles[0].d);
        if (packIa && (!articles.length || seulementLeTotal || !client.nom || !client.adr)) {
          try {
            const ia = await iaApi.lectureDocument({ fichiers: await prepareFichiers([f]) });
            const c = ia.client || {};
            client = {
              nom: client.nom || c.nom || '',
              adr: client.adr || c.adresse || '',
              npa: client.npa || c.npa || '',
            };
            const lues = (ia.lignes || []).map((l) => ({
              d: l.designation || '',
              q: l.quantite ?? 1,
              u: uniteDevis(l.unite) || 'pce',
              p: l.prixUnitaire ?? 0,
              rem: 0,
            }));
            if (lues.length && (!articles.length || seulementLeTotal)) articles = lues;
          } catch {
            // Le Pack IA n'a pas pu lire : la lecture du texte reste.
          }
        }
        const r =
          (client.nom || '').trim() && articles.length
            ? creerPiece(
                {
                  type: typeChoisi.current ? type : lu.genre || type,
                  cli: client,
                  cliId: '',
                  lignes: articles,
                  origine: origineDe(lu),
                },
                cache,
              )
            : null;
        res = r
          ? {
              fichier: f,
              etat: 'ok',
              texte:
                (r.t === 'offre' ? 'Offre' : 'Facture') +
                (r.num ? ' ' + r.num : '') +
                ' · ' +
                client.nom +
                (r.annee ? ' · ' + r.annee : ''),
              annee: r.annee,
            }
          : {
              fichier: f,
              etat: 'verifier',
              texte: !(client.nom || '').trim()
                ? 'Client non reconnu : à reprendre seule'
                : 'Aucun article ni montant lu : à reprendre seule',
            };
      } catch (e) {
        res = { fichier: f, etat: 'verifier', texte: e?.message || 'Fichier illisible' };
      }
      setLot((p) => ({ ...p, faits: [...p.faits, res] }));
    }
    setLot((p) => ({ ...p, enCours: false }));
  };
  const sansArticle = !lignes.some((l) => (l.d || '').trim());
  const totalHT = lignes.reduce((s, l) => s + num2(l.q) * num2(l.p) * (1 - num2(l.rem) / 100), 0);
  // La quantité et le prix restent tels qu'on les tape (« 12,5 », un champ
  // vide) : convertis à chaque touche, un champ vidé redevenait « 0 » et le
  // prix commençait par ce zéro. Ils ne deviennent des nombres qu'au calcul.
  const upd = (id, k, v) => setLignes((p) => p.map((l) => (l.id === id ? { ...l, [k]: v } : l)));
  /**
   * Crée la pièce reprise. Une seule (l'écran de vérification) ou en lot :
   * les clients créés pendant le lot sont retenus dans `cache`, pour qu'un
   * même client repris deux fois ne soit créé qu'une fois.
   *
   * @returns {{t: string, num: string, annee: string}|null} la pièce créée
   */
  const creerPiece = (
    { type, cli, cliId, lignes, origine },
    cache = { clients: clients || [] },
  ) => {
    let cid = cliId || '';
    // Aux longueurs que le serveur accepte : un NPA mal découpé (« 1207
    // Genève … Martin Sophie ») faisait refuser le client, et avec lui la
    // pièce, alors que l'écran annonçait « créée ». Le NPA et sa localité
    // d'abord, s'ils se lisent ; sinon le début.
    const adr = (cli.adr || '').trim().slice(0, 200);
    const npaLu = (cli.npa || '').trim();
    const npa = (
      npaLu.length > 60 ? (npaLu.match(/\b\d{4,5}\s+[^\d\n…,;]{2,40}/) || [npaLu])[0] : npaLu
    )
      .trim()
      .slice(0, 60);
    if (!cid && cli.nom.trim()) {
      const ex = cache.clients.find(
        (c) => (c.nom || '').toLowerCase() === cli.nom.trim().toLowerCase(),
      );
      if (ex) cid = ex.id;
      else {
        cid = idProvisoire();
        const nouveau = {
          id: cid,
          nom: cli.nom.trim().slice(0, 200),
          adr,
          npa,
          tel: '',
          email: '',
          remiseGlobale: 0,
          remises: {},
        };
        cache.clients = [...cache.clients, nouveau];
        setClients((p) => [...(p || []), nouveau]);
      }
    }
    // Un client déjà connu sans adresse — repris d'une autre facture où elle
    // n'avait pas été lue, ou créé à la main — reçoit celle de cette facture :
    // sinon la facture reprise restait sans coordonnées.
    const connu = cache.clients.find((c) => String(c.id) === String(cid));
    if (connu && ((!connu.adr && adr) || (!connu.npa && npa))) {
      setClients((p) =>
        (p || []).map((c) =>
          String(c.id) === String(cid) ? { ...c, adr: c.adr || adr, npa: c.npa || npa } : c,
        ),
      );
    }
    const lg = lignes
      .filter((l) => (l.d || '').trim())
      .map((l) => ({
        id: uid(),
        d: l.d.trim(),
        sd: l.sd || '',
        u: l.u || 'pce',
        q: num2(l.q),
        p: num2(l.p),
        rem: num2(l.rem),
      }));
    if (!lg.length) {
      return null;
    }
    const dateOrigine = origine.date || today();
    // Le taux de TVA de l'année de la pièce : 7.7 % avant 2024, 8.1 % depuis.
    const tva = tauxTvaParDefaut(soc, dateOrigine);
    // La remise qui retombe sur le total TTC de l'ancienne pièce, au centime.
    const remisePct = remiseVisantLeTotal(lg, tva, origine.remisePct, origine.totalTtc);
    const sect = estMag ? 'magasin' : 'batiment';
    const annee = (String(dateOrigine).match(/(\d{4})/) || [])[1] || '';
    const libelle = (t) =>
      origine.numero
        ? 'Reprise — ancienne ' + t + ' n° ' + origine.numero
        : 'Document repris (import)';
    // Le numéro de l'ancienne pièce est gardé : il est déjà chez le client.
    // La reprise prenait jusqu'ici le numéro suivant de SwissTech, et le
    // numéro d'origine n'apparaissait plus que dans le libellé. S'il est déjà
    // porté par une pièce de SwissTech, le suivant le remplace.
    const numeroRepris = (liste) => {
      const voulu = String(origine.numero || '').trim();
      if (!voulu) return undefined;
      return (liste || []).some((x) => String(x.num ?? '').trim() === voulu) ? undefined : voulu;
    };
    if (type === 'offre') {
      const num = numeroRepris(cache.devis || devis);
      if (num) cache.devis = [...(cache.devis || devis || []), { num }];
      // L'identifiant se calcule sur la liste du moment : en lot, plusieurs
      // pièces naissent avant que l'écran ne se redessine.
      setDevis((p) => [
        {
          id: nextSeq(p || []),
          ...(num ? { num } : {}),
          modeleId: 'detaille',
          clientId: cid,
          chantierId: '',
          date: dateOrigine,
          delai: '',
          validite: validiteParDefaut(soc),
          desc: libelle('offre'),
          intro: '',
          mentions: '',
          // La remise et l'acompte de l'ancienne offre la suivent.
          acompte: origine.acomptePct || 0,
          tvaPct: tva,
          remisePct,
          piedMessage: origine.remarques || '',
          statut: 'En attente',
          signDate: null,
          sect,
          lignes: lg,
        },
        ...(p || []),
      ]);
      return { t: 'offre', num: num || '', annee };
    }
    {
      const num = numeroRepris(cache.factures || factures);
      if (num) cache.factures = [...(cache.factures || factures || []), { num }];
      setFactures((p) => [
        {
          id: nextSeq(p || []),
          ...(num ? { num } : {}),
          type: 'standard',
          chantierId: '',
          clientId: cid,
          date: dateOrigine,
          // L'échéance de l'ancienne facture, en jours depuis sa date.
          echeance: delaiDeReprise(dateOrigine, origine.echeance, origine.delaiJours),
          tvaPct: tva,
          remisePct,
          // Une ancienne facture est déjà partie chez le client : envoyée à sa
          // date, et payée si on l'indique. En brouillon, elle restait hors
          // de la comptabilité.
          statut: origine.payee ? 'Payée' : 'Envoyée',
          envoye: true,
          envoyeLe: dateOrigine,
          paidDate: origine.payee ? origine.payeeLe || today() : null,
          paidAmount: origine.payee
            ? origine.montantPaye || totauxDocument(lg, tva, remisePct).ttc
            : 0,
          piedMessage: origine.remarques || '',
          desc: libelle('facture'),
          lignes: lg,
          sect,
        },
        ...(p || []),
      ]);
      return { t: 'facture', num: num || '', annee };
    }
  };
  const creer = () => {
    const r = creerPiece({ type, cli, cliId, lignes, origine });
    if (r) setDone({ t: r.t, n: r.num || '' });
  };
  const inp = {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid ' + C.border,
    borderRadius: 8,
    padding: '9px 10px',
    fontSize: 12,
  };
  // Le choix « Offre » / « Facture » : avant la lecture, et encore sur
  // l'écran de vérification, pour corriger ce que le titre a fait choisir.
  const choixDuType = (
    <>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: C.muted, marginBottom: 6 }}>
        Type de document à créer :
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[
          ['offre', 'Offre'],
          ['facture', 'Facture'],
        ].map(([v, lb]) => {
          const on = type === v;
          return (
            <div
              key={v}
              role="button"
              aria-pressed={on}
              onClick={() => choisitType(v)}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '10px',
                borderRadius: 9,
                fontSize: 11,
                fontWeight: 800,
                cursor: 'pointer',
                background: on ? C.navy : C.white,
                color: on ? '#fff' : C.grey,
                border: '1px solid ' + (on ? C.navy : C.border),
              }}
            >
              {lb}
            </div>
          );
        })}
      </div>
    </>
  );
  const motType = estMag ? 'offre' : 'offre';
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('plus')}
      pageTitle="📥 Reprise de documents"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      {lot ? (
        <Card>
          <Bar
            icon="📚"
            title={trf('Reprise en lot : {0} / {1}', lot.faits.length, lot.total)}
            color={DA}
            sub={
              lot.enCours
                ? tr('Lecture en cours… chaque pièce va dans son année.')
                : tr('Terminé. Chaque pièce est rangée à sa date d’origine.')
            }
          />
          <div style={{ padding: 13 }}>
            {!lot.enCours &&
              (() => {
                const parAnnee = {};
                lot.faits
                  .filter((x) => x.etat === 'ok')
                  .forEach((x) => {
                    parAnnee[x.annee || '?'] = (parAnnee[x.annee || '?'] || 0) + 1;
                  });
                const annees = Object.keys(parAnnee).sort();
                return annees.length ? (
                  <div
                    style={{
                      background: C.greenL,
                      borderRadius: 9,
                      padding: '8px 11px',
                      fontSize: 11,
                      fontWeight: 800,
                      color: C.green,
                      marginBottom: 10,
                    }}
                  >
                    {annees.map((a) => trf('{0} : {1} pièce(s)', a, parAnnee[a])).join(' · ')}
                  </div>
                ) : null;
              })()}
            {lot.faits.map((x, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 0',
                  borderBottom: '1px solid ' + C.border,
                }}
              >
                <span style={{ fontSize: 15 }}>{x.etat === 'ok' ? '✅' : '⚠️'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 800 }}>{x.texte}</div>
                  <div style={{ fontSize: 9, color: C.muted }}>{x.fichier?.name}</div>
                </div>
                {x.etat !== 'ok' && !lot.enCours && (
                  <Btn
                    label={tr('Vérifier')}
                    sm
                    color={C.amber}
                    onClick={() => {
                      setLot(null);
                      litFichier(x.fichier);
                    }}
                  />
                )}
              </div>
            ))}
            {lot.enCours && (
              <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', padding: 10 }}>
                ⏳ {tr('Lecture…')}
              </div>
            )}
            {!lot.enCours && (
              <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
                <Btn
                  label="→ Voir mes factures"
                  color={C.navy}
                  full
                  onClick={() => onNav('factures')}
                />
                <Btn
                  label="Reprendre d’autres documents"
                  ghost
                  color={C.grey}
                  full
                  onClick={() => setLot(null)}
                />
              </div>
            )}
          </div>
        </Card>
      ) : done ? (
        <Card>
          <div style={{ padding: 20, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.green, marginBottom: 6 }}>
              {done.t === 'offre' ? 'Offre' : 'Facture'} {done.n} créée dans SwissTech
            </div>
            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
              Le client et les articles ont été repris. Ouvre-la pour vérifier, choisir le modèle et
              générer le document A4.
            </div>
            <Btn
              label={done.t === 'offre' ? '→ Voir mes offres' : '→ Voir mes factures'}
              color={C.navy}
              full
              onClick={() => onNav(done.t === 'offre' ? 'devis' : 'factures')}
            />
            <div style={{ marginTop: 8 }}>
              <Btn
                label="Reprendre un autre document"
                ghost
                color={C.grey}
                full
                onClick={() => {
                  setDone(null);
                  setStep(1);
                  setRaw('');
                  setCli({ nom: '', adr: '', npa: '' });
                  setCliId('');
                  setAlertes([]);
                  setLignes([]);
                  setOrigine({ numero: '', date: '' });
                }}
              />
            </div>
          </div>
        </Card>
      ) : step === 1 ? (
        <>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="📥"
              title="Importer de mon ancien logiciel"
              color={DA}
              sub="Depuis un autre logiciel — colle le texte, l'app en tire le client et les articles"
            />
            <div style={{ padding: 13 }}>
              {choixDuType}
              <div style={{ fontSize: 9.5, fontWeight: 700, color: C.muted, marginBottom: 6 }}>
                Colle ici le texte de l'ancien document :
              </div>
              <textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder={
                  "Exemple :\nFacturé à : Marie Dupont\nChemin des Fleurs 12\n1004 Lausanne\n\nCarrelage 60×60   25 m²   42.00\nMain-d'œuvre   40 h   85.00\nColle   12 sac   28.50"
                }
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  minHeight: 180,
                  border: '1px solid ' + C.border,
                  borderRadius: 10,
                  padding: '11px',
                  fontSize: 11,
                  lineHeight: 1.5,
                  fontFamily: 'inherit',
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <label style={{ flexShrink: 0 }}>
                  <div
                    style={{
                      background: C.navyXL,
                      color: C.navy,
                      borderRadius: 9,
                      padding: '9px 12px',
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: 'pointer',
                      border: '1px solid ' + C.border,
                    }}
                  >
                    {lecture === LECTURE_PDF ? '⏳ Lecture du PDF…' : '📄 Fichier PDF / .txt'}
                    <input
                      type="file"
                      accept=".pdf,application/pdf,.csv,.txt,text/plain,text/csv"
                      aria-label="Ancienne pièce (PDF, .txt ou .csv)"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const fs = [...(e.target.files || [])];
                        e.target.value = '';
                        if (fs.length > 1) litPlusieurs(fs);
                        else litFichier(fs[0]);
                      }}
                    />
                  </div>
                </label>
                <div style={{ fontSize: 8, color: C.muted, lineHeight: 1.4 }}>
                  {packIa
                    ? 'Ou lis directement une photo ou un scan, ci-dessous.'
                    : 'Choisis le PDF de l’ancienne facture ou offre : le client et les lignes se remplissent. Un scan (photo) se lit avec le Pack IA.'}{' '}
                  {tr('Tu peux en choisir plusieurs d’un coup : chacune va dans son année.')}
                </div>
              </div>
              {packIa && (
                <div style={{ marginTop: 10 }}>
                  <Btn
                    label={
                      lecture === LECTURE_EN_COURS
                        ? '🤖 Lecture du document…'
                        : '📷 Photo ou scan (PDF) d’un document papier · Pack IA'
                    }
                    color={C.purple}
                    full
                    disabled={lecture === LECTURE_EN_COURS}
                    onClick={() => entreeScan.current && entreeScan.current.click()}
                  />
                  <input
                    ref={entreeScan}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    aria-label="Document papier à lire"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const fichiers = Array.from(e.target.files || []);
                      e.target.value = '';
                      if (fichiers.length) litLeScan(fichiers);
                    }}
                  />
                </div>
              )}
              {lecture && lecture !== LECTURE_EN_COURS && lecture !== LECTURE_PDF && (
                <div role="alert" style={{ fontSize: 11, color: C.red, marginTop: 6 }}>
                  {lecture}
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <Btn
                  label="🔍 Analyser le document"
                  color={DA}
                  full
                  disabled={!raw.trim()}
                  onClick={analyser}
                />
              </div>
              <div
                style={{
                  background: C.tealL,
                  borderRadius: 9,
                  padding: '10px 12px',
                  marginTop: 12,
                  fontSize: 8.5,
                  color: C.grey,
                  lineHeight: 1.6,
                }}
              >
                ℹ️ Tes coordonnées de société sont déjà dans SwissTech — inutile de les coller.
              </div>
            </div>
          </Card>
        </>
      ) : (
        <>
          <Card style={{ marginBottom: 11 }}>
            <Bar icon="👤" title="Client détecté" color={C.navy} sub="Vérifie / corrige" />
            <div style={{ padding: 13 }}>
              {choixDuType}
              {alertes.map((a) => (
                <div key={a} style={{ fontSize: 11, color: C.amber, marginBottom: 6 }}>
                  ⚠ {a}
                </div>
              ))}
              {cliId !== '' && (
                <div style={{ fontSize: 11, color: C.green, fontWeight: 700, marginBottom: 7 }}>
                  ✓ Client reconnu dans ton fichier — l'offre lui sera rattachée.
                </div>
              )}
              <div
                style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginBottom: 7 }}
              >
                <div>
                  <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>
                    N° de l’ancienne pièce
                  </div>
                  <input
                    value={origine.numero}
                    onChange={(e) => setOrigine((p) => ({ ...p, numero: e.target.value }))}
                    style={inp}
                    placeholder="—"
                  />
                </div>
                <div>
                  <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>
                    Date d’origine (JJ.MM.AAAA)
                  </div>
                  <input
                    value={origine.date}
                    onChange={(e) => setOrigine((p) => ({ ...p, date: e.target.value }))}
                    style={inp}
                    placeholder={today()}
                  />
                </div>
              </div>
              {type === 'facture' && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 7,
                    marginBottom: 7,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>
                      Échéance (JJ.MM.AAAA)
                    </div>
                    <input
                      value={origine.echeance || ''}
                      onChange={(e) => setOrigine((p) => ({ ...p, echeance: e.target.value }))}
                      style={inp}
                      placeholder="30 jours"
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 8,
                        color: C.muted,
                        marginBottom: 3,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!origine.payee}
                        onChange={(e) => setOrigine((p) => ({ ...p, payee: e.target.checked }))}
                      />
                      Déjà payée, le (JJ.MM.AAAA)
                    </label>
                    <input
                      value={origine.payeeLe || ''}
                      disabled={!origine.payee}
                      onChange={(e) => setOrigine((p) => ({ ...p, payeeLe: e.target.value }))}
                      style={inp}
                      placeholder={today()}
                    />
                  </div>
                </div>
              )}
              <div style={{ marginBottom: 7 }}>
                <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>Nom</div>
                <input
                  value={cli.nom}
                  onChange={(e) => {
                    setCliId('');
                    setCli((p) => ({ ...p, nom: e.target.value }));
                  }}
                  style={inp}
                  placeholder="Nom du client"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 7 }}>
                <div>
                  <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>Adresse</div>
                  <input
                    value={cli.adr}
                    onChange={(e) => setCli((p) => ({ ...p, adr: e.target.value }))}
                    style={inp}
                    placeholder="Rue"
                  />
                </div>
                <div>
                  <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>NPA / Ville</div>
                  <input
                    value={cli.npa}
                    onChange={(e) => setCli((p) => ({ ...p, npa: e.target.value }))}
                    style={inp}
                    placeholder="1004 Lausanne"
                  />
                </div>
              </div>
            </div>
          </Card>
          <Card style={{ marginBottom: 11 }}>
            <Bar
              icon="📋"
              title={'Articles détectés (' + lignes.length + ')'}
              color={C.teal}
              sub="Vérifie, corrige, ajoute ou supprime"
            />
            <div style={{ padding: 13 }}>
              {/*
                Aucun article lu : l'écran le disait par des cases vides et un
                bouton grisé, sans un mot. Il le dit, et propose la lecture par
                le Pack IA du même PDF quand elle est possible.
              */}
              {!sansArticle ? null : (
                <div
                  role="alert"
                  style={{
                    background: '#fff7e6',
                    border: '1px solid #f0c36d',
                    borderRadius: 9,
                    padding: '9px 11px',
                    marginBottom: 10,
                    fontSize: 11.5,
                    color: C.ink,
                    lineHeight: 1.45,
                  }}
                >
                  Aucun article n’a été reconnu dans ce document.{' '}
                  {packIa && pdfLu
                    ? 'Fais-le lire par le Pack IA, ou écris les articles ci-dessous.'
                    : 'Écris les articles ci-dessous (désignation et prix).'}
                  {packIa && pdfLu && (
                    <div style={{ marginTop: 8 }}>
                      <Btn
                        label={
                          lecture === LECTURE_EN_COURS
                            ? '🤖 Lecture du document…'
                            : '🤖 Lire les articles avec le Pack IA'
                        }
                        color={C.purple}
                        full
                        sm
                        disabled={lecture === LECTURE_EN_COURS}
                        onClick={() => litLeScan([pdfLu])}
                      />
                    </div>
                  )}
                  {lecture && lecture !== LECTURE_EN_COURS && lecture !== LECTURE_PDF && (
                    <div style={{ color: C.red, marginTop: 6 }}>{lecture}</div>
                  )}
                </div>
              )}
              {lignes.map((l) => (
                <div
                  key={l.id}
                  style={{
                    border: '1px solid ' + C.border,
                    borderRadius: 9,
                    padding: 8,
                    marginBottom: 8,
                  }}
                >
                  <input
                    value={l.d}
                    onChange={(e) => upd(l.id, 'd', e.target.value)}
                    placeholder="Désignation"
                    style={{ ...inp, marginBottom: 6, fontWeight: 600 }}
                  />
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      value={l.q}
                      onChange={(e) => upd(l.id, 'q', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      aria-label="Quantité"
                      inputMode="decimal"
                      style={{ ...inp, width: 52, textAlign: 'center', padding: '7px 4px' }}
                    />
                    <input
                      value={l.u}
                      onChange={(e) => upd(l.id, 'u', e.target.value)}
                      style={{
                        ...inp,
                        width: 60,
                        textAlign: 'center',
                        padding: '7px 4px',
                        fontSize: 10,
                      }}
                    />
                    <span style={{ fontSize: 10, color: C.muted }}>×</span>
                    <input
                      value={l.p}
                      onChange={(e) => upd(l.id, 'p', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      aria-label="Prix unitaire"
                      inputMode="decimal"
                      style={{ ...inp, flex: 1, textAlign: 'right' }}
                    />
                    <span
                      style={{ fontSize: 10, fontWeight: 700, minWidth: 64, textAlign: 'right' }}
                    >
                      {fmt(num2(l.q) * num2(l.p))}
                    </span>
                    <button
                      onClick={() => setLignes((p) => p.filter((x) => x.id !== l.id))}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: C.red,
                        fontSize: 16,
                        cursor: 'pointer',
                        padding: '0 2px',
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {(l.alertes || []).map((a) => (
                    <div key={a} style={{ fontSize: 10.5, color: C.amber, marginTop: 4 }}>
                      ⚠ {a}
                    </div>
                  ))}
                </div>
              ))}
              <Btn
                label="+ Ajouter une ligne"
                sm
                ghost
                color={C.teal}
                full
                onClick={() =>
                  setLignes((p) => [...p, { id: uid(), d: '', q: 1, u: 'pce', p: 0, rem: 0 }])
                }
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 12,
                  paddingTop: 10,
                  borderTop: '1px solid ' + C.border,
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                <span>Sous-total HT</span>
                <span>
                  {DEVISE} {fmt(totalHT)}
                </span>
              </div>
            </div>
          </Card>
          <Btn
            label={'✓ Créer ' + (type === 'offre' ? "l'offre" : 'la facture') + ' dans SwissTech'}
            color={C.green}
            full
            disabled={sansArticle}
            onClick={creer}
          />
          {sansArticle && (
            <div style={{ fontSize: 11, color: C.grey, textAlign: 'center', marginTop: 6 }}>
              Pour créer {type === 'offre' ? "l'offre" : 'la facture'}, écris au moins un article
              avec sa désignation.
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <Btn
              label="← Recommencer"
              ghost
              color={C.grey}
              full
              onClick={() => {
                setStep(1);
              }}
            />
          </div>
          <div
            style={{
              fontSize: 8,
              color: C.muted,
              marginTop: 10,
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            Après création, ouvre le document pour choisir le modèle et générer le PDF A4 avec tes
            coordonnées + QR.
          </div>
        </>
      )}
    </Shell>
  );
}
